import axios from 'axios';
import config from '../config/environment';

export interface FieldNewsDto {
  id: string;
  fieldId: string;
  title: string;
  content: string;
  authorName: string;
  isPublished: boolean;
  publishedDate: string;
  createdDate: string;
  updatedDate: string;
  isPriority: boolean;
  imageUrl?: string;
  imageUrls: string[];
  priority: number;
  linkedEventId?: string;
}

export interface CreateFieldNewsRequest {
  title: string;
  content: string;
  authorName: string;
  isPublished: boolean;
  isPriority: boolean;
  imageUrl?: string;
  imageUrls: string[];
  priority: number;
  linkedEventId?: string;
}

export interface UpdateFieldNewsRequest {
  title: string;
  content: string;
  authorName: string;
  isPublished: boolean;
  isPriority: boolean;
  imageUrl?: string;
  imageUrls: string[];
  priority: number;
  linkedEventId?: string;
}

class FieldNewsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('fieldToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async getPublicFieldNews(fieldId: string): Promise<FieldNewsDto[]> {
    try {
      const response = await axios.get(`${config.apiUrl}/api/FieldNews/field/${fieldId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get field news');
    }
  }

  async getLatestFieldNews(fieldId: string, limit: number = 5): Promise<FieldNewsDto[]> {
    try {
      const response = await axios.get(`${config.apiUrl}/api/FieldNews/field/${fieldId}/latest?limit=${limit}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get latest field news');
    }
  }

  async getAllFieldNews(fieldId: string): Promise<FieldNewsDto[]> {
    try {
      const response = await axios.get(`${config.apiUrl}/api/FieldNews/field/${fieldId}/all`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get all field news');
    }
  }

  async createFieldNews(data: CreateFieldNewsRequest, imageFile?: File): Promise<FieldNewsDto> {
    try {
      const formData = new FormData();
      
      // Добавляем все поля модели
      formData.append('title', data.title);
      formData.append('content', data.content);
      formData.append('authorName', data.authorName);
      formData.append('isPublished', data.isPublished.toString());
      formData.append('isPriority', data.isPriority.toString());
      formData.append('priority', data.priority.toString());
      
      if (data.imageUrl) {
        formData.append('imageUrl', data.imageUrl);
      }

      data.imageUrls.forEach((url, index) => {
        formData.append(`imageUrls[${index}]`, url);
      });

      if (data.linkedEventId) {
        formData.append('linkedEventId', data.linkedEventId);
      }

      // Добавляем файл если есть
      if (imageFile) {
        formData.append('imageFile', imageFile);
      }

      const response = await axios.post(`${config.apiUrl}/api/FieldNews`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fieldToken')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create field news');
    }
  }

  async updateFieldNews(newsId: string, data: UpdateFieldNewsRequest, imageFile?: File): Promise<FieldNewsDto> {
    try {
      const formData = new FormData();
      
      // Добавляем все поля модели
      formData.append('title', data.title);
      formData.append('content', data.content);
      formData.append('authorName', data.authorName);
      formData.append('isPublished', data.isPublished.toString());
      formData.append('isPriority', data.isPriority.toString());
      formData.append('priority', data.priority.toString());
      
      if (data.imageUrl) {
        formData.append('imageUrl', data.imageUrl);
      }

      data.imageUrls.forEach((url, index) => {
        formData.append(`imageUrls[${index}]`, url);
      });

      if (data.linkedEventId) {
        formData.append('linkedEventId', data.linkedEventId);
      }

      // Добавляем файл если есть
      if (imageFile) {
        formData.append('imageFile', imageFile);
      }

      const response = await axios.put(`${config.apiUrl}/api/FieldNews/${newsId}`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fieldToken')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update field news');
    }
  }

  async deleteFieldNews(newsId: string): Promise<void> {
    try {
      await axios.delete(`${config.apiUrl}/api/FieldNews/${newsId}`, {
        headers: this.getAuthHeaders()
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete field news');
    }
  }

  async getNewsByEventId(eventId: string): Promise<FieldNewsDto | null> {
    try {
      const response = await axios.get(`${config.apiUrl}/api/FieldNews/by-event/${eventId}`);
      return response.data;
    } catch (error: any) {
      // Return null if no news found for this event
      if (error.response?.status === 404) {
        return null;
      }
      throw new Error(error.response?.data?.message || 'Failed to get news by event');
    }
  }

}

const fieldNewsService = new FieldNewsService();
export default fieldNewsService;